'use client';
import {useState, useEffect, useMemo} from 'react';
import {useUpdateRecaptcha} from '@damascus-components/Recaptcha/Recaptcha';
import {Modal} from '@components/Modal';
import {Link} from '@components/Link';
import * as yup from 'yup';
import {yupResolver} from '@hookform/resolvers/yup';
import {Controller, SubmitHandler, useForm, useWatch} from 'react-hook-form';
import {useParams} from 'next/navigation';
import {hasCaptchaSiteKey} from '@utils/getCaptchaSiteKey';
import {LANGUAGE_OPTIONS} from '@launch-components/IntlProvider/IntlProvider';
import {isValidPhoneNumber} from '@utils/validation.utils';
import {requestLeadGenerationAPI} from '@services/launches/requestLeadGeneration';
import {useSearchParamsCustom} from '@utils/searchparams.uitilites';
import {logEvent} from '@utils/googleAnalytics';
import {getCountryCodeAndNameFromValue} from '@services/properties/getLeadGenerationOptions';
import {NoTranslateWrapper} from '@components/NoTranslateWrapper';
import SuccessMessage from './SuccessMessage';
import cn from './style.module.scss';
import {CountrySelect} from '@components/CountrySelect';
import React from 'react';
import AldarLogo from '@components/AldarLogo';
import {Box} from '@mui/material';
import useLeadMetaQuery from '@hooks/querys/useLeadMeta';

type FormType = {
  firstName: string;
  lastName: string;
  email: string;
  countryCode: string;
  contact: string;
  buyingReason: string;
  consent: boolean;
  country: string;
  city?: string;
  responseKey?: string;
  comment?: string;
  receiveUpdates?: boolean;
  availability?: string;
};
const DEFAULT_FORM_VALUES = {
  countryCode: '+971___United Arab Emirates',
  buyingReason: '',
  consent: false,
  firstName: '',
  lastName: '',
  email: '',
  contact: '',
  residentStatus: '',
  nationality: '',
  responseKey: '',
  comment: '',
  receiveUpdates: false,
  country: 'United Arab Emirates',
  city: 'Abu Dhabi',
};

const DEFAULT_GA_KEYS = {
  category: 'Aldar Websites',
  trigger: 'Damascus',
};
interface CustomRegistrationProps {
  t: {[key: string]: any};
  variant?: string;
  countryCodes: Record<string, string>[];
  nationalities: Record<string, string>[];
  successPopUpLink: string;
  privacyPolicyLink: string;
  termsAndConditionsLink: string;
  className?: string;
  listClassName?: string;
  blockClassName?: string;
  authClassName?: string;
  inputClassName?: string;
  submitBlockClassName?: string;
  submitClassName?: string;
  checkboxClassName?: string;
  marksClassName?: string;
  contentClassName?: string;
  buttonClassName?: string;
  projectName: string;
  formId: string;
  formType: string;
  showLogo?: boolean;
  validationClassName?: string;
}

export function CustomRegistration({
  variant = 'default',
  className = '',
  listClassName = '',
  blockClassName = '',
  authClassName = '',
  inputClassName = '',
  validationClassName = '',
  submitClassName = '',
  submitBlockClassName = '',
  checkboxClassName = '',
  marksClassName = '',
  contentClassName = '',
  buttonClassName = '',

  ...props
}: CustomRegistrationProps) {
  const [status, setStatus] = React.useState<'idle' | 'loading' | 'success'>(
    'idle'
  );

  const leadMeta = useLeadMetaQuery().data;
  const countryList = leadMeta?.nationalities;

  const {lang} = useParams();
  const urlParams = useSearchParamsCustom();
  const {t} = props;
  const isFormStarted = React.useRef(false);
  const {getRecaptchaToken} = useUpdateRecaptcha();

  const schema = yup.object({
    firstName: yup
      .string()
      .required('First name is required')
      .max(35, 'First name should be 35 characters long')
      .matches(
        /^[A-Za-z\s\u0600-\u06FF]+$/,
        'First name should contain only letters and spaces'
      )
      .trim(),
    lastName: yup
      .string()
      .required('Last name is required')
      .max(35, 'Last name should be 35 characters long')
      .matches(
        /^[A-Za-z\s\u0600-\u06FF]+$/,
        'Last name should contain only letters and spaces'
      )
      .trim(),
    email: yup
      .string()
      .required('Email is required')
      .email('Invalid email')
      .matches(
        /^[a-zA-Z0-9]{1,}[a-zA-Z0-9._+-]*@(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/,
        'Invalid email'
      )
      .trim(),
    countryCode: yup.string().required().trim(),
    contact: yup
      .string()
      .required('Phone number is required')
      .test(
        'Phone Number Validity Check',
        'Not a valid phone number',
        function (number: string) {
          const {code} = getCountryCodeAndNameFromValue(
            this.parent.countryCode
          );
          return isValidPhoneNumber(number, code);
        }
      )
      .test(
        'Phone Number Leading Zero validation check',
        'Please remove the leading 0 from your phone number.',
        function (number: string) {
          return !number.trim().startsWith('0');
        }
      )
      .matches(/^\d{1,20}$/, 'Phone number must be digits only')
      .trim(),
    country: yup.string().required('Country is required'),
    city: yup.string().when('country', {
      is: (val: string) => val === 'United Arab Emirates',
      then: (schema) => schema.required('City is required'),
      otherwise: (schema) => schema.optional(),
    }),
    buyingReason: yup.string().required('This field is required'),
    consent: yup.boolean().required(),
    receiveUpdates: yup.boolean().optional(),
    availability: t['availabilityOptions']
      ? yup.string().required('This field is required')
      : yup.string().optional(),
    responseKey: yup.string(),
  });

  const {
    handleSubmit,
    register,
    control,
    setValue,
    trigger,
    setError,
    unregister,
    reset,
    watch,
    formState: {errors},
  } = useForm<FormType>({
    defaultValues: DEFAULT_FORM_VALUES,
    resolver: yupResolver(schema),
  });

  const country = watch('country');
  const city = watch('city');
  const countryCityList = useMemo(() => {
    if (!country) return [];
    return leadMeta?.cities?.[country] ?? [];
  }, [leadMeta?.cities, country]);

  useEffect(() => {
    if (country === 'United Arab Emirates') {
      setValue('city', 'Abu Dhabi');
    } else {
      setValue('city', '');
    }
  }, [country]);

  const onSelectCountryCode = (value: string) => {
    setValue('countryCode', value);
    trigger('contact');
  };
  const getAdditionalParams = () => {
    const enquiryCategory = urlParams.get('enquiryCategory');
    const enquiryTrigger = urlParams.get('enquiryTrigger');
    const offer = urlParams.get('offer');
    const salesOrigin = urlParams.get('salesOrigin');
    const leadType = urlParams.get('leadType');
    const corporateWealthName = urlParams.get('corporateWealthName');
    const campaignName = urlParams.get('campaignname');
    return {
      enquiryCategory: enquiryCategory ?? undefined,
      enquiryTrigger: enquiryTrigger ?? undefined,
      offerCode: offer ?? undefined,
      salesOrigin: salesOrigin ?? undefined,
      leadType: leadType ?? undefined,
      corporateWealthName: corporateWealthName ?? undefined,
      campaignName: campaignName ?? undefined,
    };
  };
  const handleFormStart = () => {
    if (isFormStarted.current) return;
    isFormStarted.current = true;
    logEvent({
      event: 'form_start',
      additionalInfo: {
        form_id: props.formId,
        form_type: props.formType,
      },
    });
  };
  const onSubmitForm: SubmitHandler<FormType> = async (data) => {
    setStatus('loading');
    if (!data.consent) {
      setStatus('idle');
      setError('consent', {
        message:
          'Please agree to the Privacy Policy & Terms of Use to proceed.',
      });
      return;
    }
    const responseKey = await getRecaptchaToken();
    if (hasCaptchaSiteKey() && !responseKey) {
      setStatus('idle');
      setError('responseKey', {
        message: 'Please refresh the page and try again.',
      });
      return;
    }
    try {
      const paramsKeys = getAdditionalParams();
      const response = await requestLeadGenerationAPI({
        ...data,
        comment: data.comment ? data.comment : data.availability,
        ...paramsKeys,
        responseKey: responseKey,
        countryCode: getCountryCodeAndNameFromValue(data.countryCode).code,
        language: LANGUAGE_OPTIONS.find(({value}) => value === lang)?.label,
        project: props.projectName,
      }).getResponse();

      logEvent({
        event: 'form_success',
        additionalInfo: {
          lead_id: response?.data?.leadId,
          form_id: props.formId,
          form_type: props.formType,
          Project_Name: props.projectName,
          Trigger: paramsKeys.enquiryTrigger ?? DEFAULT_GA_KEYS.trigger,
          Category: paramsKeys.enquiryCategory ?? DEFAULT_GA_KEYS.category,
        },
      });
      setStatus('success');
    } catch (e: any) {
      setStatus('idle');
      setError('responseKey', {
        message: e.message
          ? e.message
          : 'Something went wrong, Please try again.',
      });
    }
  };
  const commonProps = {disabled: status === 'loading' ? true : false};
  React.useEffect(() => {
    if (!hasCaptchaSiteKey()) {
      unregister('responseKey');
    }
  }, []);
  const handleFormClose = () => {
    setStatus('idle');
    reset(undefined, {keepDefaultValues: true});
  };
  const [selectedValue, setSelectedValue] = useState('');
  const options = [
    {value: '', label: `${t['reasonToBuy']} *`, disabled: true},
    {value: t['reasonBuyOptionInvestor'], label: t['reasonBuyOptionInvestor']},
    {
      value: t['reasonBuyOptionHomeOwner'],
      label: t['reasonBuyOptionHomeOwner'],
    },
  ];
  return (
    <>
      {status === 'success' ? (
        variant === 'modal' ? (
          <Modal
            isOpen={true}
            close={true}
            onClose={handleFormClose}
            className={cn.registrationSuccessModel}>
            <SuccessMessage
              successPopUpLink={props.successPopUpLink}
              t={t}
              ContentClassName={contentClassName}
              ButtonClassName={buttonClassName}
            />
          </Modal>
        ) : (
          <div className={cn.registrationSuccessBlock}>
            <SuccessMessage
              t={t}
              successPopUpLink={props.successPopUpLink}
              ContentClassName={contentClassName}
              ButtonClassName={buttonClassName}
            />
          </div>
        )
      ) : (
        <NoTranslateWrapper>
          <div
            className={`${cn.launchRegistrationBlock} ${className}`}
            onBlur={handleFormStart}
            id='launchGRDContact'>
            <ul
              className={`${cn.launchRegistrationList} ${listClassName}`}
              id='launchYASRegister'>
              <li>
                <div className={`${cn.registerSideBlock} ${blockClassName}`}>
                  <div className={cn.registerSideItem}>
                    <div className={cn.registerElementPane}>
                      <input
                        type='text'
                        data-testid={'lf_firstName'}
                        className={`${cn.registrationInputsBlocks} ${inputClassName}`}
                        id='fname'
                        placeholder={`${t['firstName']} *`}
                        {...register('firstName')}
                        {...commonProps}
                      />
                      {errors.firstName?.message && (
                        <p className={cn.registrationValidationMessage}>
                          {errors.firstName?.message}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className={cn.registerSideItem}>
                    <div className={cn.registerElementPane}>
                      <input
                        type='text'
                        data-testid={'lf_lastName'}
                        className={`${cn.registrationInputsBlocks} ${inputClassName}`}
                        id='lname'
                        placeholder={`${t['lastName']} *`}
                        {...register('lastName')}
                        {...commonProps}
                      />
                      {errors.lastName?.message && (
                        <p className={cn.registrationValidationMessage}>
                          {errors.lastName?.message}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </li>
              <li>
                <div className={cn.registerElementPane}>
                  <input
                    type='text'
                    id='email'
                    data-testid={'lf_email'}
                    className={`${cn.registrationInputsBlocks} ${inputClassName}`}
                    placeholder={`${t['email']} *`}
                    {...register('email')}
                    {...commonProps}
                  />
                  {errors.email?.message && (
                    <p className={cn.registrationValidationMessage}>
                      {errors.email?.message}
                    </p>
                  )}
                </div>
              </li>
              <li>
                <div className={cn.registerElementPane}>
                  <div
                    className={`${cn.registerPhoneBlock} ${cn.registrationInputsBlocks} ${inputClassName}`}>
                    <div className={cn.phoneCountryElement}>
                      <Controller
                        name='countryCode'
                        control={control}
                        render={({field}) => (
                          <CountrySelect
                            {...field}
                            onChange={onSelectCountryCode}
                            options={props.countryCodes}
                            {...commonProps}
                          />
                        )}
                      />
                    </div>
                    <div
                      className={cn.phoneInputElement}
                      onBlur={() => trigger('contact')}>
                      <input
                        type='tel'
                        data-testid={'lf_contact'}
                        inputMode='tel'
                        pattern='[0-9]{10,15}'
                        maxLength={15}
                        className={`${cn.registrationInputsBlocks} ${cn.phoneInputs} ${inputClassName}`}
                        placeholder={`${t['phoneNumber']} *`}
                        {...register('contact', {
                          required: true,
                          pattern: /^[0-9]{10,15}$/,
                        })}
                        {...commonProps}
                      />
                    </div>
                  </div>
                  {errors.contact?.message && (
                    <p className={cn.registrationValidationMessage}>
                      {errors.contact?.message}
                    </p>
                  )}
                </div>
              </li>

              <li>
                <div className={cn.registerElementPane}>
                  <select
                    id='country'
                    data-testid={'lf_country'}
                    className={`${cn.registrationInputsBlocks} ${inputClassName}`}
                    value={country}
                    {...register('country')}
                    placeholder={`${t['country']} *`}
                    {...commonProps}>
                    {countryList?.map((option) => (
                      <NoTranslateWrapper key={option.value}>
                        <option
                          style={{color: '#455564', lineHeight: '22px'}}
                          value={option.value}>
                          {option.label}
                        </option>
                      </NoTranslateWrapper>
                    ))}
                  </select>
                  {errors.country?.message && (
                    <p
                      className={`${cn.registrationValidationMessage} ${validationClassName}`}>
                      {errors.country?.message}
                    </p>
                  )}
                </div>
              </li>

              <li>
                <div className={cn.registerElementPane}>
                  <select
                    id='City'
                    data-testid={'lf_city'}
                    className={`${cn.registrationInputsBlocks} ${inputClassName}`}
                    value={city}
                    {...register('city')}
                    placeholder={`${t['city']} ${
                      country === 'United Arab Emirates' ? '*' : ''
                    }`}
                    {...commonProps}>
                    <NoTranslateWrapper>
                      <option
                        style={{
                          color: '#455564',
                          lineHeight: '22px',
                          opacity: '.5',
                        }}
                        value=''>
                        {`${t['city'] || 'Select your city'} ${
                          country === 'United Arab Emirates' ? '*' : ''
                        }`}
                      </option>
                    </NoTranslateWrapper>
                    {countryCityList?.map((option) => (
                      <NoTranslateWrapper key={option.value}>
                        <option
                          style={{color: '#455564', lineHeight: '22px'}}
                          value={option.value}>
                          {option.label}
                        </option>
                      </NoTranslateWrapper>
                    ))}
                  </select>
                  {errors.city?.message && (
                    <p
                      className={`${cn.registrationValidationMessage} ${validationClassName}`}>
                      {errors.city?.message}
                    </p>
                  )}
                </div>
              </li>

              <li>
                <div className={cn.registerElementPane}>
                  <select
                    id='buyingReason'
                    data-testid={'lf_buyingReason'}
                    className={`${cn.registrationInputsBlocks} ${inputClassName}`}
                    defaultValue={''}
                    {...register('buyingReason')}
                    placeholder={`${t['reasonToBuy']} *`}
                    {...commonProps}>
                    <NoTranslateWrapper>
                      <option
                        style={{color: '#455564', lineHeight: '22px'}}
                        value=''
                        disabled>
                        {`${t['reasonToBuy']} *`}
                      </option>
                    </NoTranslateWrapper>
                    <NoTranslateWrapper>
                      <option
                        style={{color: '#455564', lineHeight: '22px'}}
                        value='Investor'>
                        {t['reasonBuyOptionInvestor']}
                      </option>
                    </NoTranslateWrapper>
                    <NoTranslateWrapper>
                      <option
                        style={{color: '#455564', lineHeight: '22px'}}
                        value='End User'>
                        {t['reasonBuyOptionHomeOwner']}
                      </option>
                    </NoTranslateWrapper>
                  </select>
                  {errors.buyingReason?.message && (
                    <p className={cn.registrationValidationMessage}>
                      {errors.buyingReason?.message}
                    </p>
                  )}
                </div>
              </li>
              {t['availabilityOptions'] && (
                <li>
                  <div className={cn.registerElementPane}>
                    <select
                      id='availability'
                      data-testid={'lf_availability'}
                      className={`${cn.registrationInputsBlocks} ${inputClassName}`}
                      defaultValue={''}
                      {...register('availability')}
                      placeholder={`${t['availability']} *`}
                      {...commonProps}>
                      {t['availabilityOptions']?.map(
                        (option: {value: string; label: string}) => (
                          <NoTranslateWrapper key={option.value}>
                            <option
                              style={{color: '#455564', lineHeight: '22px'}}
                              value={option.value}>
                              {option.label}
                            </option>
                          </NoTranslateWrapper>
                        )
                      )}
                    </select>
                    {errors.availability?.message && (
                      <p className={cn.registrationValidationMessage}>
                        {errors.availability?.message}
                      </p>
                    )}
                  </div>
                </li>
              )}
            </ul>
            <div
              className={`${cn.registrationAuthentication} ${authClassName}`}>
              <div className={cn.authenticationElement}>
                <div className={cn.authenticationItem}>
                  <label
                    className={`${cn.registrationCheckbox} ${checkboxClassName}`}>
                    <input
                      type='checkbox'
                      data-testid={'lf_receiveUpdates'}
                      id='receiveUpdates'
                      {...register('receiveUpdates')}
                      {...commonProps}
                    />
                    <span
                      className={`${cn.checkboxMarks} ${marksClassName}`}></span>
                  </label>
                  <p>{t['recieveUpdates']}</p>
                </div>
              </div>
              <div className={cn.authenticationElement}>
                <div className={cn.authenticationItem}>
                  <label
                    className={`${cn.registrationCheckbox} ${checkboxClassName}`}>
                    <input
                      type='checkbox'
                      data-testid={'lf_consent'}
                      id='consent'
                      value='true'
                      {...register('consent')}
                      {...commonProps}
                    />
                    <span
                      className={`${cn.checkboxMarks} ${marksClassName}`}></span>
                  </label>
                  <p>
                    <span>{t['consentLabel']} </span>
                    <Link url={props.privacyPolicyLink} target={'_blank'}>
                      {t['privacyPolicy']}
                    </Link>
                    <span>{` & `}</span>
                    <Link url={props.termsAndConditionsLink} target={'_blank'}>
                      {t['termsAndConditions']}
                    </Link>
                    <span> {t['consentLabelEnd']}</span>
                  </p>
                </div>

                {errors.responseKey?.message && (
                  <p className={cn.authenticationValidationMessage}>
                    {errors.responseKey?.message}
                  </p>
                )}
                {errors.consent?.message && (
                  <p className={cn.authenticationValidationMessage}>
                    {errors.consent?.message}
                  </p>
                )}
              </div>
            </div>
            <div
              className={`${cn.registrationSubmitBlock} ${submitBlockClassName}`}>
              <button
                className={`${cn.registerSubmitButton} ${submitClassName}`}
                data-testid={'lf_submit'}
                type='submit'
                onClick={handleSubmit(onSubmitForm)}
                {...commonProps}>
                <span>
                  {!commonProps.disabled ? t['actionButton'] : 'Loading...'}
                </span>
              </button>
              {props.showLogo && (
                <Box
                  component='div'
                  sx={{
                    'flex': '0 0 auto',
                    'display': 'flex',
                    'flexDirection': 'column',
                    'alignItems': 'center',
                    'justifyContent': 'center',
                    'minWidth': {
                      xs: '50px',
                      sm: '60px',
                      md: '70px',
                    },
                    'maxWidth': {
                      xs: '50px',
                      sm: '60px',
                      md: '70px',
                    },
                    '& > a': {
                      height: '70px',
                    },
                  }}>
                  <Link
                    url='https://www.aldar.com/properties/en'
                    target='_blank'>
                    <AldarLogo color='#000' />
                  </Link>
                </Box>
              )}
            </div>
          </div>
        </NoTranslateWrapper>
      )}
    </>
  );
}
